import { useEffect, useRef } from "react";
import { product } from "@/mocks/product";

export default function LookbookSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.classList.add("animate-fade-in");
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const lookbookImages = [
    {
      src: product.images[0].src,
      alt: "Flower Printed Set — front view",
      label: "Front",
    },
    {
      src: product.images[1].src,
      alt: "Flower Printed Set — back view",
      label: "Back",
    },
    {
      src: product.images[2].src,
      alt: "Flower Printed Set — side view",
      label: "Side",
    },
  ];

  const marqueeImages = [...lookbookImages, ...lookbookImages];

  return (
    <section ref={sectionRef} className="py-10 md:py-16 bg-background-100 overflow-hidden">
      <style>{`
        @keyframes lookbook-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>

      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="text-center mb-8 md:mb-10">
          <span className="text-sm font-medium text-foreground-500 tracking-wide uppercase">
            Lookbook
          </span>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-accent-800 mt-2">
            Every angle, every detail
          </h2>
        </div>
      </div>

      <div className="relative group">
        <div
          className="flex gap-3 md:gap-4 group-hover:[animation-play-state:paused]"
          style={{
            animation: "lookbook-scroll 40s linear infinite",
            width: "max-content",
          }}
        >
          {marqueeImages.map((img, index) => (
            <div
              key={`${img.label}-${index}`}
              className="flex-shrink-0 w-[220px] md:w-[280px] rounded-xl overflow-hidden aspect-[3/4] group/card"
            >
              <img
                src={img.src}
                alt={img.alt}
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover/card:scale-105"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-3 md:p-4">
                <span className="text-white text-xs font-medium uppercase tracking-wider">
                  {img.label}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="absolute left-0 top-0 bottom-0 w-12 md:w-24 bg-gradient-to-r from-background-100 to-transparent pointer-events-none z-10" />
        <div className="absolute right-0 top-0 bottom-0 w-12 md:w-24 bg-gradient-to-r from-transparent to-background-100 pointer-events-none z-10" />
      </div>
    </section>
  );
}